import { Box, Stack, Typography, Button, TextField, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { FoodsIcon } from "@foodpilot/foods";
import { InfoRow } from "./InfoRow";
import { useCompanyGeneralForm } from "../hooks/useCompanyGeneralForm";
import { getCompanyGeneralFormFields } from "../config/companyGeneralFormFields";
import { useFoodpilotContext, MODIFY_ORG_SETTINGS, ORGANIZATION } from "src/context/FoodpilotContext";

const CompanyGeneralForm = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    const {
        control,
        formState,
        onSubmit,
        isEditing,
        setIsEditing,
        isCompanyLoading,
        isSaving,
        isPaymentEnabled,
        company,
        defaultValues,
        reset,
    } = useCompanyGeneralForm();

    const fields = getCompanyGeneralFormFields(isPaymentEnabled);
    const { canUser } = useFoodpilotContext();
    const canModify = canUser(MODIFY_ORG_SETTINGS, ORGANIZATION);

    return (
        <Box sx={{ backgroundColor: "white", border: "1px solid #E0DEDD", borderRadius: theme.spacing(2) }}>
            <Stack
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    borderBottom: "1px solid #E0DEDD",
                    px: theme.spacing(4),
                    py: theme.spacing(3),
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: theme.spacing(2), md: 0 },
                    alignItems: { xs: "flex-start", md: "center" },
                }}
            >
                <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                    {t("companySettings.general.information.title", "Information")}
                </Typography>
                {canModify &&
                    (!isEditing ?
                        <Button
                            variant="primary"
                            onClick={() => setIsEditing(true)}
                            sx={{
                                boxShadow: "0px 4px 8px 0px rgba(42, 43, 39, 0.08)",
                                textTransform: "none",
                                fontWeight: 700,
                                display: "flex",
                                gap: theme.spacing(1),
                                alignItems: "center",
                            }}
                        >
                            <FoodsIcon icon="edit" size={2} />
                            {t("common.edit", "Edit")}
                        </Button>
                    :   <Stack direction="row" gap={theme.spacing(1)}>
                            <Button
                                variant="ternary"
                                onClick={() => {
                                    reset(defaultValues);
                                    setIsEditing(false);
                                }}
                                disabled={isSaving}
                            >
                                {t("common.cancel", "Cancel")}
                            </Button>
                            <Button variant="primary" onClick={onSubmit} disabled={!formState.isValid || isSaving}>
                                {isSaving ? t("common.saving", "Saving...") : t("common.save", "Save")}
                            </Button>
                        </Stack>)}
            </Stack>

            {!isEditing || !canModify ?
                isCompanyLoading || !company ?
                    <Box>
                        {fields.map((f, idx) => (
                            <Stack
                                key={f.name}
                                sx={{
                                    minHeight: "64px",
                                    borderBottom: idx === fields.length - 1 ? "none" : "1px solid #E0DEDD",
                                    px: theme.spacing(4),
                                    py: theme.spacing(1.5),
                                    flexDirection: { xs: "column", md: "row" },
                                    alignItems: { xs: "flex-start", md: "center" },
                                    gap: { xs: theme.spacing(2), md: 0 },
                                    justifyContent: { xs: "flex-start", md: "flex-start" },
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        width: { xs: "100%", md: "321px" },
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {f.label}
                                    {f.required && (
                                        <Typography component="span" sx={{ color: "error.main", ml: 0.5 }}>
                                            *
                                        </Typography>
                                    )}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="rounded" height={40} sx={{ width: { xs: 250, md: 349 } }} />
                                </Box>
                            </Stack>
                        ))}
                    </Box>
                :   <Box>
                        {fields.map((f, idx) => (
                            <InfoRow
                                key={f.name}
                                label={f.label}
                                value={String(defaultValues[f.name] ?? "")}
                                isLast={idx === fields.length - 1}
                                required={!!f.required}
                            />
                        ))}
                    </Box>

            :   <Box>
                    {fields.map((f, idx) => (
                        <Stack
                            key={f.name}
                            direction={{ xs: "column", md: "row" }}
                            alignItems="center"
                            sx={{
                                borderBottom: idx === fields.length - 1 ? "none" : "1px solid #E0DEDD",

                                flexDirection: { xs: "column", md: "row" },
                                display: "flex",
                                alignItems: { xs: "flex-start", md: "center" },
                                gap: theme.spacing(2),
                                py: { xs: theme.spacing(2), md: 0 },
                                px: theme.spacing(4),
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    width: { xs: "100%", md: "321px" },
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {f.label}
                                {f.required && (
                                    <Typography component="span" sx={{ color: "error.main", ml: theme.spacing(0.5) }}>
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <Box sx={{ flex: 1, width: { xs: "100%", md: "auto" }, mx: theme.spacing(1) }}>
                                <Controller
                                    name={f.name as any}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            size="small"
                                            fullWidth
                                            sx={{
                                                maxWidth: { xs: "100%", md: 349 },
                                                my: { xs: 0, md: theme.spacing(2) },
                                                mx: theme.spacing(-1),
                                                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: theme.palette.error.main,
                                                },
                                                "& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor: theme.palette.error.main,
                                                    },
                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                            }}
                                            disabled={!canModify}
                                            required={!!f.required}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message ?? undefined}
                                        />
                                    )}
                                    /*
                                            @TODO: Replace the component above by the new field input provided by @foodpilot/foods ^2.7.17 when the
                                                   Payment RC is released
                                             <FoodsTextInput 
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                disabled={!canModify}
                                                error={!!fieldState.error}
                                                ExtraInputProps={{
                                                    required: !!f.required,
                                                }}
                                            />
                                        */
                                />
                            </Box>
                        </Stack>
                    ))}
                </Box>
            }
        </Box>
    );
};

export default CompanyGeneralForm;
